import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import api from '../services/api.service';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: null,
    color: '#2196F3',
    icon: 'Category',
    sortOrder: 0
  });

  useEffect(() => {
    let mounted = true;
    
    const loadCategoriesWithCleanup = async () => {
      await loadCategories(mounted);
    };
    
    loadCategoriesWithCleanup();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      mounted = false;
    };
  }, []);

  const loadCategories = async (mounted = true) => {
    try {
      if (!mounted) return;
      setLoading(true);
      setError(null);
      
      // Load hierarchical categories
      const response = await api.get('/categories');
      if (!mounted) return;
      setCategories(response.data.data || []);
      
      // Load flat list for parent selection
      const flatResponse = await api.get('/categories?flat=true');
      if (!mounted) return;
      setFlatCategories(flatResponse.data.data || []);
    } catch (err) {
      if (!mounted) return;
      setError('Failed to load categories');
      console.error('Error loading categories:', err);
    } finally {
      if (mounted) {
        setLoading(false);
      }
    }
  };

  const handleToggleExpand = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      parentId: null,
      color: '#2196F3',
      icon: 'Category',
      sortOrder: 0
    });
    setDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      parentId: category.parentId,
      color: category.color || '#2196F3',
      icon: category.icon || 'Category',
      sortOrder: category.sortOrder || 0
    });
    setDialogOpen(true);
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      setError(null);
      
      if (!formData.name.trim()) {
        setError('Category name is required');
        return;
      }
      
      if (editingCategory) {
        // Update existing category
        await api.put(`/categories/${editingCategory.id}`, formData);
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await api.post('/categories', formData);
        setSuccess('Category created successfully');
      }
      
      setDialogOpen(false);
      loadCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      await api.delete(`/categories/${categoryToDelete.id}`);
      setSuccess('Category deleted successfully');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      loadCategories();
    } catch (err) {
      if (err.response?.data?.requiresReassignment) {
        setError(`Cannot delete: ${err.response.data.error}`);
      } else {
        setError(err.response?.data?.error || 'Failed to delete category');
      }
      setDeleteDialogOpen(false);
    }
  };

  const renderCategory = (category, level = 0) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    
    return (
      <React.Fragment key={category.id}>
        <TableRow hover>
          <TableCell sx={{ pl: level * 4 + 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasSubcategories && (
                <IconButton size="small" onClick={() => handleToggleExpand(category.id)}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
              {!hasSubcategories && <Box sx={{ width: 40 }} />}
              <CircleIcon sx={{ fontSize: 16, color: category.color || '#ccc' }} />
              <Typography variant="body2">{category.name}</Typography>
            </Box>
          </TableCell>
          <TableCell>{category.description}</TableCell>
          <TableCell align="center">
            <Chip 
              label={category.icon || 'Category'} 
              size="small" 
              variant="outlined"
            />
          </TableCell>
          <TableCell align="center">{category.sortOrder}</TableCell>
          <TableCell align="right">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEditCategory(category)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDeleteCategory(category)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
        {hasSubcategories && isExpanded && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {category.subcategories.map(sub => renderCategory(sub, level + 1))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  if (loading && categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading categories...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Equipment Categories</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCategory}
        >
          Add Category
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Icon</TableCell>
              <TableCell align="center">Sort Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No categories yet. Click "Add Category" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              categories.map(category => renderCategory(category))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            
            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={formData.parentId || ''}
                onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
                label="Parent Category"
              >
                <MenuItem value="">None (Top Level)</MenuItem>
                {flatCategories
                  .filter(cat => cat.id !== editingCategory?.id)
                  .map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                sx={{ width: 120 }}
              />
              
              <TextField
                label="Icon Name"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                fullWidth
                helperText="Material UI icon name"
              />
            </Box>
            
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              fullWidth
              helperText="Lower numbers appear first"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category "{categoryToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManager;